/**
 * Analyte History Chart — TradingView-style.
 *
 * Built on `lightweight-charts` v5 so the user can zoom, pan, and pinch
 * through the full historical record without the chart re-rendering on every
 * gesture. One line series per (compound, well) for historical samples; a
 * matching dashed series per (compound, well) for v3.2 predictions with a
 * marker on each horizon point (3mo / 1yr / 2yr / 5yr / 10yr / 20yr for BTEX,
 * 3mo / 1yr / 2yr for TPH).
 *
 * Color is determined ONLY by compound — multiple wells of the same compound
 * overlap; the user identifies a specific series via the crosshair tooltip.
 *
 * Time axis quirk: `lightweight-charts` requires strictly ascending UNIQUE
 * timestamps inside `setData()`. We dedupe by the YYYY-MM-DD prefix before
 * pushing each series, so two ISO timestamps on the same calendar day
 * collapse to one entry (the first wins).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  LineSeries,
  LineStyle,
  CrosshairMode,
  PriceScaleMode,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { ChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { polygonApi, AnalyticalHistoryResponse, TimelinePredictionsResponse } from '../../api/polygon';
import { getCompoundProperties } from './MultiContaminantPlumeUtils';

interface AnalyteHistoryChartProps {
  siteId: string;
  /** Optional controlled compound visibility. When provided, the analyte filter
   *  shares state with the page-level legend so toggles stay synchronized. */
  visibleCompounds?: Set<string>;
  onVisibleCompoundsChange?: (next: Set<string>) => void;
}

interface SeriesPoint {
  time: UTCTimestamp;
  value: number;
}

interface SeriesPack {
  /** `${compoundLower}::${well}` */
  key: string;
  compound: string;
  well: string;
  color: string;
  historical: SeriesPoint[];
  /** Includes the historical endpoint as the first point so the dashed
   *  prediction line visually continues from the solid one. */
  prediction: SeriesPoint[];
  /** Calendar-horizon offsets for the prediction points (used to label markers). */
  predictionLabels: string[];
}

function isoToUtcSec(iso: string): UTCTimestamp {
  // YYYY-MM-DD → midnight UTC seconds. lightweight-charts is happy with that.
  return Math.floor(new Date(iso + 'T00:00:00Z').getTime() / 1000) as UTCTimestamp;
}

function formatConc(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1) return value.toFixed(2);
  if (value >= 0.01) return value.toFixed(3);
  if (value >= 1e-4) return value.toFixed(5);
  return value.toExponential(1);
}

function horizonLabelFromDays(days?: number | null): string {
  switch (days) {
    case 90: return '3mo';
    case 365: return '1yr';
    case 730: return '2yr';
    case 1825: return '5yr';
    case 3650: return '10yr';
    case 7300: return '20yr';
    default: return days != null ? `${days}d` : '';
  }
}

// The dedicated forecast horizons surfaced in the Well Predictions table.
// Only these get a node + axis label on the chart; intermediate monthly slices
// stay in the prediction line but are left unmarked.
const HORIZON_LABELS = new Set(['3mo', '1yr', '2yr', '5yr', '10yr', '20yr']);

/**
 * Canonicalize a compound name OR v3.2 analyte code to a single key so
 * historical samples ("Benzene") and prediction rows ("B") collapse into
 * the same series pack. Without this the chart shows prediction lines as
 * separate ghosts that don't bridge back to the historical solid line.
 */
const COMPOUND_CANONICAL: Record<string, string> = {
  // BTEX — v3.2 short codes
  'b': 'benzene',
  't': 'toluene',
  'e': 'ethylbenzene',
  'x': 'xylenes',
  'm,p-xylenes': 'xylenes',
  'o-xylene': 'xylenes',
  'total xylenes': 'xylenes',
  'total btex': 'total btex',
  // BTEX — display names
  'benzene': 'benzene',
  'toluene': 'toluene',
  'ethylbenzene': 'ethylbenzene',
  'ethyl benzene': 'ethylbenzene',
  'xylenes': 'xylenes',
  'xylene': 'xylenes',
  // TPH — v3.2 carbon-range codes
  'tph-gro': 'c6-c12',
  'tph-dro': 'c12-c28',
  'c6-c12': 'c6-c12',
  '>c12-c28': 'c12-c28',
  'c12-c28': 'c12-c28',
  '>c28-c35': 'c28-c35',
  'c28-c35': 'c28-c35',
  'c6-c35': 'total tph',
  'total tph': 'total tph',
  'tph': 'total tph',
};

const COMPOUND_DISPLAY: Record<string, string> = {
  'benzene': 'Benzene',
  'toluene': 'Toluene',
  'ethylbenzene': 'Ethylbenzene',
  'xylenes': 'Xylenes',
  'total btex': 'Total BTEX',
  'c6-c12': 'C6-C12 (GRO)',
  'c12-c28': 'C12-C28 (DRO)',
  'c28-c35': 'C28-C35',
  'total tph': 'Total TPH',
};

function canonicalCompound(raw: string): string {
  const n = raw.toLowerCase().trim();
  return COMPOUND_CANONICAL[n] || n;
}

function displayCompound(canonical: string, fallback: string): string {
  return COMPOUND_DISPLAY[canonical] || fallback;
}

export function AnalyteHistoryChart({
  siteId,
  visibleCompounds,
  onVisibleCompoundsChange,
}: AnalyteHistoryChartProps) {
  const [data, setData] = useState<AnalyticalHistoryResponse | null>(null);
  const [predictions, setPredictions] = useState<TimelinePredictionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenWells, setHiddenWells] = useState<Set<string>>(new Set());
  const [showPredictions, setShowPredictions] = useState(true);
  const [logScale, setLogScale] = useState(true);
  const [showWellList, setShowWellList] = useState(false);
  const [internalVisibleCompounds, setInternalVisibleCompounds] = useState<Set<string>>(new Set());

  // Chart refs — kept across renders so we can mutate series in place.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const predSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const markersRef = useRef<Map<string, ISeriesMarkersPluginApi<Time>>>(new Map());

  // Tooltip overlay state — populated by the crosshair subscription.
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([
      polygonApi.getAnalyticalHistory(siteId),
      polygonApi.getTimelinePredictions(siteId),
    ]).then(([historyResult, predResult]) => {
      if (cancelled) return;
      if (historyResult.status === 'fulfilled') {
        setData(historyResult.value);
      } else {
        setError(historyResult.reason?.message || 'Failed to load analytical history');
      }
      setPredictions(predResult.status === 'fulfilled' ? predResult.value : null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [siteId]);

  // ── Build per-(compound, well) packs from the API responses ──────────
  const packs = useMemo<SeriesPack[]>(() => {
    if (!data) return [];
    const byKey = new Map<string, SeriesPack>();
    // Track latest historical (date, value) per key so we can seed the
    // prediction series at that exact point — bridges the dashed forecast
    // back to the solid historical line.
    const lastHist = new Map<string, { time: UTCTimestamp; value: number; iso: string }>();

    for (const [compound, points] of Object.entries(data.data)) {
      const canonical = canonicalCompound(compound);
      for (const p of points) {
        if (!p.date || !p.well_id) continue;
        if (p.is_non_detect) continue;
        if (!Number.isFinite(p.concentration) || p.concentration <= 0) continue;
        const iso = String(p.date).slice(0, 10);
        const key = `${canonical}::${p.well_id}`;
        let pack = byKey.get(key);
        if (!pack) {
          const display = displayCompound(canonical, compound);
          pack = {
            key,
            compound: display,
            well: p.well_id,
            color: getCompoundProperties(display).plume_color || '#94a3b8',
            historical: [],
            prediction: [],
            predictionLabels: [],
          };
          byKey.set(key, pack);
        }
        // analytical_results.concentration is stored in mg/L (schema default
        // + the platform-wide units convention in CLAUDE.md). No conversion.
        // Predictions arrive as predicted_conc_mgl below — same unit, so the
        // historical line and the dashed forecast share the same y-axis.
        const value = p.concentration;
        const time = isoToUtcSec(iso);
        if (!Number.isFinite(time as number)) continue; // malformed date — skip
        pack.historical.push({ time, value });

        const prev = lastHist.get(key);
        if (!prev || iso > prev.iso) {
          lastHist.set(key, { time, value, iso });
        }
      }
    }

    // Wire predictions into existing packs (or create new packs if a
    // compound was forecast for a well that has no detections in history).
    // Predictions arrive as v3.2 analyte CODES ("B", "TPH-GRO") whereas
    // historical samples arrive as DISPLAY names ("Benzene", "C6-C12") —
    // we canonicalize both sides so they land in the same pack.
    if (predictions) {
      for (const slice of predictions.months || []) {
        const horizonLabel = (slice.horizon_label || horizonLabelFromDays(slice.horizon_days)).trim();
        for (const wp of slice.predictions || []) {
          if (!wp.target_date || !wp.well_name || !wp.analyte_code) continue;
          // Reject <= 0: a log y-axis can't plot zero/negative (log(0) = -Inf
          // crashes lightweight-charts → black screen), and a 0 forecast is
          // effectively a non-detect (historical non-detects are dropped too).
          if (!Number.isFinite(wp.predicted_conc_mgl) || wp.predicted_conc_mgl <= 0) continue;
          const canonical = canonicalCompound(wp.analyte_code);
          const key = `${canonical}::${wp.well_name}`;
          let pack = byKey.get(key);
          if (!pack) {
            const display = displayCompound(canonical, wp.analyte_code);
            pack = {
              key,
              compound: display,
              well: wp.well_name,
              color: getCompoundProperties(display).plume_color || '#94a3b8',
              historical: [],
              prediction: [],
              predictionLabels: [],
            };
            byKey.set(key, pack);
          }
          const iso = String(wp.target_date).slice(0, 10);
          const time = isoToUtcSec(iso);
          if (!Number.isFinite(time as number)) continue; // malformed date — skip
          pack.prediction.push({ time, value: wp.predicted_conc_mgl });
          pack.predictionLabels.push(horizonLabel);
        }
      }

      // Bridge: seed the prediction line at the historical endpoint.
      for (const [key, last] of lastHist.entries()) {
        const pack = byKey.get(key);
        if (!pack) continue;
        if (pack.prediction.length === 0) continue;
        if (pack.prediction[0].time > last.time) {
          pack.prediction.unshift({ time: last.time, value: last.value });
          pack.predictionLabels.unshift('now');
        }
      }
    }

    // Dedupe + sort each series. lightweight-charts requires strictly
    // ascending UNIQUE timestamps — keep the first value per timestamp.
    for (const pack of byKey.values()) {
      pack.historical = dedupeSortAsc(pack.historical);
      // Predictions share the dedupe rule (horizon dates are unique anyway).
      const sortedPred = pack.prediction
        .map((p, i) => ({ ...p, label: pack.predictionLabels[i] }))
        .sort((a, b) => (a.time as number) - (b.time as number));
      const seenTs = new Set<number>();
      const cleanPred: { time: UTCTimestamp; value: number; label: string }[] = [];
      for (const p of sortedPred) {
        const t = p.time as unknown as number;
        if (seenTs.has(t)) continue;
        seenTs.add(t);
        cleanPred.push(p);
      }
      pack.prediction = cleanPred.map(p => ({ time: p.time, value: p.value }));
      pack.predictionLabels = cleanPred.map(p => p.label);
    }

    return Array.from(byKey.values());
  }, [data, predictions]);

  // ── Effective compound visibility — controlled or internal ───────────
  const allCompounds = useMemo(() => {
    const s = new Set<string>();
    for (const p of packs) s.add(p.compound);
    return Array.from(s).sort();
  }, [packs]);

  // One-time seed of the visible-compound set. Without this the chart renders
  // blank whenever the parent hands us a controlled-but-empty set (e.g. the
  // user opens the Analyte tab before visiting the map tabs that normally
  // populate it). Ref-guarded so the user can still hide every compound later.
  const didSeedCompounds = useRef(false);
  useEffect(() => {
    if (allCompounds.length === 0) return;
    if (visibleCompounds) {
      if (visibleCompounds.size === 0 && !didSeedCompounds.current && onVisibleCompoundsChange) {
        didSeedCompounds.current = true;
        onVisibleCompoundsChange(new Set(allCompounds.map(c => c.toLowerCase())));
      }
    } else if (internalVisibleCompounds.size === 0) {
      setInternalVisibleCompounds(new Set(allCompounds.map(c => c.toLowerCase())));
    }
  }, [allCompounds, visibleCompounds, internalVisibleCompounds.size, onVisibleCompoundsChange]);

  const effectiveVisibleCompounds = visibleCompounds ?? internalVisibleCompounds;

  // ── Well list, numeric-aware sort ────────────────────────────────────
  const allWells = useMemo(() => {
    const s = new Set<string>();
    for (const p of packs) s.add(p.well);
    return Array.from(s).sort((a, b) => {
      const na = parseInt(a.replace(/\D/g, ''), 10);
      const nb = parseInt(b.replace(/\D/g, ''), 10);
      if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
      return a.localeCompare(b);
    });
  }, [packs]);

  // ── Mount the chart once and dispose on unmount ──────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: '#0a0a0a' },
        textColor: '#9ca3af',
        fontSize: 11,
        // Hide the bundled TradingView watermark in the lower-right corner.
        // The Apache-2.0 license only obliges us to credit them somewhere in
        // the product, not to display the logo on every chart instance.
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1f2937' },
        horzLines: { color: '#1f2937' },
      },
      rightPriceScale: {
        borderColor: '#374151',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: '#374151',
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 8,
        barSpacing: 8,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#06b6d4', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#06b6d4' },
        horzLine: { color: '#06b6d4', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#06b6d4' },
      },
      handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
    });
    chartRef.current = chart;

    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = containerRef.current;
      if (!tip || !container) return;
      if (
        !param.point ||
        !param.time ||
        param.point.x < 0 ||
        param.point.y < 0 ||
        !param.seriesData ||
        param.seriesData.size === 0
      ) {
        tip.style.display = 'none';
        return;
      }

      // Build tooltip lines from whichever series are under the crosshair.
      const dateMs = (param.time as number) * 1000;
      const dateStr = new Date(dateMs).toISOString().slice(0, 10);
      const lines: string[] = [`<div class="text-mono-400 text-[10px]">${dateStr}</div>`];

      param.seriesData.forEach((sd, sapi) => {
        // sd is { time, value } for line series.
        const value = (sd as { value?: number }).value;
        if (value == null || !Number.isFinite(value)) return;
        const meta = (sapi.options() as { title?: string }).title || '';
        const color = (sapi.options() as { color?: string }).color || '#94a3b8';
        lines.push(
          `<div class="flex items-center gap-1.5 text-[11px]">
             <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block"></span>
             <span class="text-mono-200">${meta}</span>
             <span class="text-cyan-300 ml-auto">${formatConc(value)} mg/L</span>
           </div>`,
        );
      });

      if (lines.length <= 1) {
        tip.style.display = 'none';
        return;
      }

      tip.innerHTML = lines.join('');
      tip.style.display = 'block';
      const { width: cw, height: ch } = container.getBoundingClientRect();
      const tw = tip.offsetWidth || 220;
      const th = tip.offsetHeight || 80;
      let x = param.point.x + 12;
      let y = param.point.y + 12;
      if (x + tw > cw) x = param.point.x - tw - 12;
      if (y + th > ch) y = param.point.y - th - 12;
      tip.style.left = `${Math.max(4, x)}px`;
      tip.style.top = `${Math.max(4, y)}px`;
    });

    return () => {
      seriesRef.current.clear();
      predSeriesRef.current.clear();
      markersRef.current.clear();
      chart.remove();
      chartRef.current = null;
    };
  }, []);

  // ── Sync price-scale mode when log toggle changes ────────────────────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.priceScale('right').applyOptions({
      mode: logScale ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal,
    });
  }, [logScale]);

  // ── Sync series with packs + visibility ──────────────────────────────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const wantedKeys = new Set<string>();
    const wantedPredKeys = new Set<string>();

    for (const pack of packs) {
      const wellVisible = !hiddenWells.has(pack.well);
      const compoundVisible = effectiveVisibleCompounds.has(pack.compound.toLowerCase());
      const visible = wellVisible && compoundVisible;
      const title = `${pack.compound} · ${pack.well}`;

      try {
      // Historical series.
      if (visible && pack.historical.length > 0) {
        let s = seriesRef.current.get(pack.key);
        if (!s) {
          s = chart.addSeries(LineSeries, {
            color: pack.color,
            lineWidth: 2,
            lineStyle: LineStyle.Solid,
            lastValueVisible: false,
            priceLineVisible: false,
            title,
            crosshairMarkerRadius: 4,
          });
          seriesRef.current.set(pack.key, s);
        } else {
          s.applyOptions({ color: pack.color, title, visible: true });
        }
        s.setData(pack.historical);
        wantedKeys.add(pack.key);
      }

      // Prediction series (dashed) + horizon markers.
      if (showPredictions && visible && pack.prediction.length > 1) {
        let s = predSeriesRef.current.get(pack.key);
        if (!s) {
          s = chart.addSeries(LineSeries, {
            color: pack.color,
            lineWidth: 2,
            lineStyle: LineStyle.Dashed,
            lastValueVisible: false,
            priceLineVisible: false,
            title: `${title} (pred)`,
            crosshairMarkerRadius: 4,
          });
          predSeriesRef.current.set(pack.key, s);
        } else {
          s.applyOptions({ color: pack.color, title: `${title} (pred)`, visible: true });
        }
        s.setData(pack.prediction);

        // Nodes only at the dedicated forecast horizons (3mo…20yr), matching
        // the Well Predictions table. Intermediate monthly slices and the
        // synthetic 'now' bridge stay in the line but get no node. The horizon
        // name sits beneath the point so it reads against the time axis rather
        // than as a floating label on every prediction point.
        const markers = pack.prediction
          .map((pt, i) => ({ pt, label: pack.predictionLabels[i] }))
          .filter(({ label }) => HORIZON_LABELS.has(label))
          .map(({ pt, label }) => ({
            time: pt.time,
            position: 'belowBar' as const,
            color: pack.color,
            shape: 'circle' as const,
            size: 1,
            text: label,
          }));
        let mp = markersRef.current.get(pack.key);
        if (!mp) {
          mp = createSeriesMarkers(s, markers);
          markersRef.current.set(pack.key, mp);
        } else {
          mp.setMarkers(markers);
        }
        wantedPredKeys.add(pack.key);
      }
      } catch (err) {
        // A single malformed series (bad timestamps, log-scale edge case) must
        // not blank or crash the entire chart — skip it and render the rest.
        console.warn('[AnalyteHistoryChart] skipped series', pack.key, err);
      }
    }

    // Hide series that aren't wanted (cheaper than removing + re-adding).
    for (const [k, s] of seriesRef.current.entries()) {
      if (!wantedKeys.has(k)) s.applyOptions({ visible: false });
    }
    for (const [k, s] of predSeriesRef.current.entries()) {
      if (!wantedPredKeys.has(k)) s.applyOptions({ visible: false });
    }

    chart.timeScale().fitContent();
  }, [packs, hiddenWells, effectiveVisibleCompounds, showPredictions]);

  // ── Render ───────────────────────────────────────────────────────────
  const setAllWells = (visible: boolean) => setHiddenWells(visible ? new Set() : new Set(allWells));
  const toggleWell = (well: string) =>
    setHiddenWells(prev => {
      const next = new Set(prev);
      if (next.has(well)) next.delete(well); else next.add(well);
      return next;
    });
  const setAllCompounds = (visible: boolean) => {
    const next = visible ? new Set(allCompounds.map(c => c.toLowerCase())) : new Set<string>();
    if (onVisibleCompoundsChange) onVisibleCompoundsChange(next);
    if (!visibleCompounds) setInternalVisibleCompounds(next);
  };

  const hasPredictions = (predictions?.months?.length ?? 0) > 0;
  const showEmptyState = !loading && !error && packs.length === 0;
  const visibleCompoundCount = effectiveVisibleCompounds ? effectiveVisibleCompounds.size : 0;

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
          Analyte Concentration History
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowWellList(v => !v)}
            className="px-2 py-0.5 text-[10px] rounded bg-mono-800 text-mono-400 border border-mono-700 hover:text-mono-300"
          >
            Wells ({allWells.length - hiddenWells.size}/{allWells.length})
          </button>
          <button
            onClick={() => setLogScale(v => !v)}
            className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
              logScale
                ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300'
                : 'bg-mono-800 border-mono-700 text-mono-400'
            }`}
            title="Toggle log/linear scale"
          >
            {logScale ? 'Log' : 'Linear'}
          </button>
          {hasPredictions && (
            <button
              onClick={() => setShowPredictions(v => !v)}
              className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                showPredictions
                  ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300'
                  : 'bg-mono-800 border-mono-700 text-mono-400'
              }`}
            >
              Predictions {showPredictions ? 'On' : 'Off'}
            </button>
          )}
          <span className="text-[10px] text-mono-500">
            {data?.total_records.toLocaleString()} records · {allWells.length} wells · {visibleCompoundCount} of {allCompounds.length} compounds
          </span>
        </div>
      </div>

      {/* Wells filter (collapsible) */}
      {showWellList && (
        <div className="bg-mono-800/40 rounded p-2">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-mono-500 uppercase tracking-wider">Wells</span>
            <button onClick={() => setAllWells(true)} className="text-[9px] text-cyan-400 hover:text-cyan-300">All</button>
            <button onClick={() => setAllWells(false)} className="text-[9px] text-mono-500 hover:text-mono-300">None</button>
            <span className="text-[9px] text-mono-600 ml-auto">
              {allWells.length - hiddenWells.size}/{allWells.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {allWells.map(well => {
              const hidden = hiddenWells.has(well);
              return (
                <button
                  key={well}
                  onClick={() => toggleWell(well)}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    hidden
                      ? 'bg-mono-900 text-mono-600 border-mono-800'
                      : 'bg-mono-800 text-mono-200 border-mono-700 hover:border-mono-500'
                  }`}
                >
                  {well}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chart — container is always mounted so the chart can size correctly.
          Loading / error / empty states are overlaid on top. */}
      <div className="relative">
        <div
          ref={containerRef}
          className="w-full h-[420px] bg-mono-950 rounded border border-mono-800"
        />
        <div
          ref={tooltipRef}
          className="absolute pointer-events-none bg-mono-950/95 border border-mono-700 rounded px-2 py-1.5 space-y-0.5 shadow-lg z-10 max-w-[280px]"
          style={{ display: 'none', position: 'absolute' }}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-mono-950/60 rounded">
            <ArrowPathIcon className="w-6 h-6 text-mono-500 animate-spin" />
            <span className="ml-3 text-mono-500 text-sm tracking-wider">LOADING ANALYTICAL HISTORY...</span>
          </div>
        )}
        {!loading && error && (
          <div className="absolute inset-0 flex items-center justify-center bg-mono-950/80 rounded text-center">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        {showEmptyState && (
          <div className="absolute inset-0 flex items-center justify-center bg-mono-950/80 rounded">
            <div className="text-center">
              <ChartBarIcon className="h-12 w-12 text-mono-600 mx-auto mb-2" />
              <p className="text-mono-500 text-sm tracking-wider">NO ANALYTICAL DATA</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-[9px] text-mono-600">
        Scroll / pinch to zoom · drag to pan · double-click axis to auto-fit ·
        {hasPredictions ? ' dashed lines + dots are v3.2 horizon predictions' : ' no v3.2 predictions for this site yet'}
      </p>
    </div>
  );
}

function dedupeSortAsc(pts: SeriesPoint[]): SeriesPoint[] {
  if (pts.length === 0) return pts;
  // Drop any non-finite time / value before sorting — lightweight-charts
  // setData() asserts on NaN and on non-ascending order, and a single bad
  // point takes down the whole chart (black screen).
  const clean = pts.filter(
    (p) => Number.isFinite(p.time as number) && Number.isFinite(p.value),
  );
  const sorted = [...clean].sort((a, b) => (a.time as number) - (b.time as number));
  const out: SeriesPoint[] = [];
  let lastT = -1;
  for (const p of sorted) {
    const t = p.time as unknown as number;
    if (t === lastT) continue;
    out.push(p);
    lastT = t;
  }
  return out;
}
